import { z } from "zod";

export const MODEL_PROVIDERS = ["anthropic", "gemini", "openai", "local", "baseten"] as const;
export type ModelProvider = (typeof MODEL_PROVIDERS)[number];
export const MODEL_PROVIDER_LABELS: Record<ModelProvider, string> = {
  anthropic: "Anthropic",
  gemini: "Gemini",
  openai: "OpenAI",
  local: "Local",
  baseten: "Baseten",
};

/**
 * Classify a *recorded* model name — `ThreadEventInference["meta"]["model"]`, as
 * written by `formatResolvedModel` — into the provider that served it.
 *
 * That field renders as `"<resolved> (<alias>)"`, so it is not a closed enum:
 * real values include `"claude-sonnet-4-6 (simple)"` and `"claude-haiku-4-5
 * (tiny)"`. Match on prefix — comparing against `ProviderModel` slugs silently
 * misses the suffixed forms. This runs the opposite direction from
 * `MODELS_BY_PROVIDER` and the `*ModelFromPicker` helpers, which map the picker's
 * closed vocabulary onto Ax enums.
 *
 * Returns `undefined` for a name no provider claims, rather than throwing: the
 * value is read back off stored jsonb that isn't re-validated on read, so an
 * unfamiliar — or malformed — model must never take a thread down. Callers decide
 * what unknown provenance means for them, and should treat reaching it as a
 * signal that a provider has renamed something.
 */
export function providerFromModelName(model: string | undefined): ModelProvider | undefined {
  // Falsy rather than `=== undefined`: `meta.model` is `.optional()`, not
  // `.nullable()`, but it is read back from jsonb via an unchecked cast, so a
  // stored `null` would be type-invisible here and throw on `.startsWith`.
  if (!model) return undefined;
  // Baseten first. Its wire ids are `<org>/<Model>` slugs rather than a family
  // prefix we can name, and open models are routinely published under an
  // `openai/…` org — matching those against the closed providers' prefixes
  // first would hand an OSS model the wrong provenance.
  if (BASETEN_WIRE_MODEL_IDS.some((id) => model.startsWith(id))) return "baseten";
  if (model.startsWith("claude")) return "anthropic";
  if (model.startsWith("gemini")) return "gemini";
  if (model.startsWith("gpt")) return "openai";
  return undefined;
}

/**
 * Selectable agent versions. The omnibox `ModelSwitcher` auto-hides its
 * Agent Version row while this list has only one entry, so a future
 * version becomes user-selectable just by adding it here.
 */
export const AGENT_VERSIONS = ["v4"] as const;
export type AgentVersion = (typeof AGENT_VERSIONS)[number];
export const AGENT_VERSION_LABELS: Record<AgentVersion, string> = {
  v4: "v4",
};

/**
 * Fully-versioned Anthropic slugs (mirrors Gemini's explicit-version
 * convention — no generation-agnostic bare slugs). Bare `sonnet`/`opus`/`haiku`
 * persist only as accepted-but-hidden legacy values (see `ANTHROPIC_LEGACY_MODELS`).
 */
export const ANTHROPIC_MODELS = [
  "sonnet-5.5",
  "sonnet-5",
  "opus-5.5",
  "opus-4.8",
  "haiku-5.5",
] as const;
export const GEMINI_MODELS = ["flash-3", "flash-3.5", "pro-3"] as const;
/**
 * OpenAI's frontier line. gpt-5.4-mini is the cheap workhorse tier (there is no
 * gpt-5.5-mini) and gpt-5.5 the standard flagship. The 5.6 generation splits a
 * single flagship into three price/capability tiers — sol, terra, luna, in
 * descending order of both. OpenAI also publishes a bare `gpt-5.6` that routes
 * to sol; it's omitted here so the picker doesn't offer the same model twice.
 * GPT-6 renames the tiers astra, sol, luna: GPT-6 Sol is priced where GPT-5.6
 * Terra was, and there is no GPT-6 Terra. Sol is already on 6.1 while Luna is
 * still on 6.
 */
export const OPENAI_MODELS = [
  "gpt-5.4-mini",
  "gpt-5.5",
  "gpt-5.6-sol",
  "gpt-5.6-terra",
  "gpt-5.6-luna",
  "gpt-6.1-sol",
  "gpt-6-luna",
] as const;
export type OpenAIModel = (typeof OPENAI_MODELS)[number];

/**
 * The `local` provider offers exactly one slug. Which weights it names is a
 * deployment detail of whatever server `LOCAL_LLM_BASE_URL` points at, not a
 * choice the picker can make — so there is nothing to enumerate here, and the
 * slug stays stable as the developer swaps models behind it.
 */
export const LOCAL_MODELS = ["local"] as const;

/**
 * Open models served through Baseten's shared Model APIs. Kept deliberately
 * small — this is a lane for A/B-ing the actor against an open model, not a
 * mirror of Baseten's catalog. Adding one means adding its slug here and its
 * wire id below.
 */
export const BASETEN_MODELS = [
  "glm-5.2",
  "glm-5.3",
  "glm-5.3-fast",
  "glm-5.3-flash",
  "kimi-k3",
  "deepseek-v4-pro",
  "deepseek-v4.1-flash",
  "deepseek-v4.1-flash-fast",
  "muse-spark-open",
  "nemotron-3.5-super",
  "gpt-oss-120b",
] as const;
export type BasetenModel = (typeof BASETEN_MODELS)[number];

/**
 * Picker slug → the model id Baseten's OpenAI-compatible endpoint expects.
 * Lives here rather than in `ai/util/models.ts` (where the other providers map
 * their slugs onto Ax enums) because Baseten has no Ax enum to map onto, and
 * because `providerFromModelName` above classifies recorded inference events
 * against these exact strings — one copy, both directions.
 */
export const BASETEN_WIRE_MODELS: Record<BasetenModel, string> = {
  "glm-5.2": "zai-org/GLM-5.2",
  "glm-5.3": "zai-org/GLM-5.3",
  "glm-5.3-fast": "zai-org/GLM-5.3-Fast",
  "glm-5.3-flash": "zai-org/GLM-5.3-Flash",
  "kimi-k3": "moonshotai/Kimi-K3",
  "deepseek-v4-pro": "deepseek-ai/DeepSeek-V4-Pro-0813",
  "deepseek-v4.1-flash": "deepseek-ai/DeepSeek-V4.1-Flash-1017",
  "deepseek-v4.1-flash-fast": "deepseek-ai/DeepSeek-V4.1-Flash-1017-Fast",
  "muse-spark-open": "meta-models/muse-spark-open",
  "nemotron-3.5-super": "nvidia/NVIDIA-Nemotron-3.5-Super-VL-120B-A12B",
  "gpt-oss-120b": "openai/gpt-oss-120b",
};

const BASETEN_WIRE_MODEL_IDS: readonly string[] = Object.values(BASETEN_WIRE_MODELS);

/**
 * Legacy anthropic slugs that validators (Zod, resolver) still accept so stored
 * thread configs keep parsing: the bare slugs from before the versioned-slug
 * rename, and the models without adaptive thinking that left the picker.
 * Hidden from the picker UI. Each resolves to its family's current adaptive
 * model through `resolveLegacyModel`, so every
 * Anthropic model the actor can reach takes `config.effort`.
 */
export const ANTHROPIC_LEGACY_MODELS = [
  "sonnet",
  "opus",
  "haiku",
  "sonnet-4.6",
  "haiku-4.5",
] as const;
export const ANTHROPIC_MODELS_ACCEPTED = [...ANTHROPIC_MODELS, ...ANTHROPIC_LEGACY_MODELS] as const;

/**
 * Legacy Gemini slugs accepted so stored thread configs keep parsing.
 * Hidden from the picker UI and resolved to their family's Gemini 3 model
 * by `geminiModelFromPicker` in `ai/util/models.ts`, so every Gemini model
 * the actor can reach supports `thinkingLevel`.
 */
export const GEMINI_LEGACY_MODELS = ["flash", "pro", "flash-2.5", "pro-2.5"] as const;
export const GEMINI_MODELS_ACCEPTED = [...GEMINI_MODELS, ...GEMINI_LEGACY_MODELS] as const;

export const PROVIDER_MODELS = [
  ...ANTHROPIC_MODELS_ACCEPTED,
  ...GEMINI_MODELS_ACCEPTED,
  ...OPENAI_MODELS,
  ...LOCAL_MODELS,
  ...BASETEN_MODELS,
] as const;
export type ProviderModel = (typeof PROVIDER_MODELS)[number];

const LEGACY_MODEL_REPLACEMENTS: Partial<Record<ProviderModel, ProviderModel>> = {
  sonnet: "sonnet-5.5",
  "sonnet-4.6": "sonnet-5.5",
  opus: "opus-5.5",
  haiku: "haiku-5.5",
  "haiku-4.5": "haiku-5.5",
  flash: "flash-3",
  "flash-2.5": "flash-3",
  pro: "pro-3",
  "pro-2.5": "pro-3",
};

/** Maps saved retired selections to the same current model in the picker and backend. */
export function resolveLegacyModel(model: ProviderModel): ProviderModel {
  return LEGACY_MODEL_REPLACEMENTS[model] ?? model;
}

// Picker (UI) options per provider — excludes legacy slugs so they don't
// appear in the dropdown.
export const MODELS_BY_PROVIDER: Record<ModelProvider, readonly ProviderModel[]> = {
  anthropic: ANTHROPIC_MODELS,
  gemini: GEMINI_MODELS,
  openai: OPENAI_MODELS,
  local: LOCAL_MODELS,
  baseten: BASETEN_MODELS,
};

// Backend validation lookup — accepts both active and legacy slugs so
// persisted thread configs and Statsig values don't fail safeParse.
export const MODELS_BY_PROVIDER_ACCEPTED: Record<ModelProvider, readonly ProviderModel[]> = {
  anthropic: ANTHROPIC_MODELS_ACCEPTED,
  gemini: GEMINI_MODELS_ACCEPTED,
  openai: OPENAI_MODELS,
  local: LOCAL_MODELS,
  baseten: BASETEN_MODELS,
};

export const PROVIDER_MODEL_LABELS: Record<ProviderModel, string> = {
  "sonnet-5.5": "Sonnet 5.5",
  "sonnet-5": "Sonnet 5",
  "sonnet-4.6": "Sonnet 4.6 (legacy)",
  "opus-5.5": "Opus 5.5",
  "opus-4.8": "Opus 4.8",
  "haiku-5.5": "Haiku 5.5",
  "haiku-4.5": "Haiku 4.5 (legacy)",
  "flash-2.5": "Flash 2.5 (legacy)",
  "flash-3": "Flash 3",
  "flash-3.5": "Flash 3.5",
  "pro-2.5": "Pro 2.5 (legacy)",
  "pro-3": "Pro 3",
  sonnet: "Sonnet (legacy)",
  opus: "Opus (legacy)",
  haiku: "Haiku (legacy)",
  flash: "Flash (legacy)",
  pro: "Pro (legacy)",
  "gpt-5.4-mini": "GPT-5.4 Mini",
  "gpt-5.5": "GPT-5.5",
  "gpt-5.6-sol": "GPT-5.6 Sol",
  "gpt-5.6-terra": "GPT-5.6 Terra",
  "gpt-5.6-luna": "GPT-5.6 Luna",
  "gpt-6.1-sol": "GPT-6.1 Sol",
  "gpt-6-luna": "GPT-6 Luna",
  local: "Local model",
  "glm-5.2": "GLM-5.2",
  "glm-5.3": "GLM-5.3",
  "glm-5.3-fast": "GLM-5.3 Fast",
  "glm-5.3-flash": "GLM-5.3 Flash",
  "kimi-k3": "Kimi K3",
  "deepseek-v4-pro": "DeepSeek V4 Pro",
  "deepseek-v4.1-flash": "DeepSeek V4.1 Flash",
  "deepseek-v4.1-flash-fast": "DeepSeek V4.1 Flash Fast",
  "muse-spark-open": "Muse Spark Open",
  "nemotron-3.5-super": "Nemotron Super 3.5",
  "gpt-oss-120b": "gpt-oss-120b",
};

/**
 * The model the picker pre-selects when the user flips to a given provider.
 * `as const` keeps each entry's literal type intact, so it stays assignable to
 * its own arm of `actorModelSchema` rather than widening to `ProviderModel`.
 */
export const DEFAULT_MODEL_BY_PROVIDER = {
  anthropic: "sonnet-5.5",
  gemini: "flash-3",
  openai: "gpt-5.5",
  local: "local",
  baseten: "deepseek-v4.1-flash",
} as const satisfies Record<ModelProvider, ProviderModel>;

/**
 * The actor model every thread uses unless a per-thread preference or an
 * `actor-model-override` rule says otherwise. This is the production default:
 * change it here, not in Statsig. `thinkingTokenBudget` is the actor LLM's
 * reasoning budget; on adaptive models it additionally maps to a native
 * Anthropic effort level (see `resolveAnthropicEffort`).
 */
export const DEFAULT_ACTOR_MODEL = {
  provider: "anthropic",
  model: DEFAULT_MODEL_BY_PROVIDER["anthropic"],
  thinkingTokenBudget: "medium",
} as const satisfies ActorModel;

/**
 * Thinking-budget ladder accepted by Ax for the actor's underlying LLM.
 * Same string union as Ax AxAIServiceOptions (can't reference that in this
 * file). Used by Gemini + OpenAI; Anthropic uses its native effort vocab
 * (`ANTHROPIC_EFFORT_LEVELS`) instead.
 */
export const THINKING_TOKEN_BUDGET_LEVELS = [
  "none",
  "minimal",
  "low",
  "medium",
  "high",
  "highest",
] as const;

export type ThinkingTokenBudgetLevel = (typeof THINKING_TOKEN_BUDGET_LEVELS)[number];

/**
 * Anthropic's native reasoning-effort vocabulary — exactly Ax's
 * `AxAIAnthropicEffortLevel`. The values pass straight through as
 * `config.effort` on the Anthropic service (see `getActorModel` in
 * `ai/util/models.ts`). There is no `none`: adaptive Claude models can't
 * disable thinking via effort (Ax always emits `{type:"adaptive"}` for them),
 * so `low` is the floor. Kept in sync with Ax by hand (the `types/` dir may not
 * import Ax).
 */
export const ANTHROPIC_EFFORT_LEVELS = ["low", "medium", "high", "xhigh", "max"] as const;

export type AnthropicEffortLevel = (typeof ANTHROPIC_EFFORT_LEVELS)[number];

/** Narrows a resolved thinking level to Anthropic's effort vocab. */
export const isAnthropicEffortLevel = (level: ThinkingLevel): level is AnthropicEffortLevel =>
  (ANTHROPIC_EFFORT_LEVELS as readonly ThinkingLevel[]).includes(level);

/** Union of every provider's thinking vocab (Anthropic effort ∪ the ladder). */
export type ThinkingLevel = ThinkingTokenBudgetLevel | AnthropicEffortLevel;

/**
 * Deduped runtime list of every thinking level, derived from the two source
 * vocabs. The provider-agnostic per-thread override + defaults schemas
 * validate against this; the discriminated `actorModelSchema` pins each
 * provider to its own vocab.
 */
export const THINKING_LEVELS: readonly ThinkingLevel[] = [
  ...new Set<ThinkingLevel>([...THINKING_TOKEN_BUDGET_LEVELS, ...ANTHROPIC_EFFORT_LEVELS]),
];

export const modelPreferencesSchema = z
  .object({
    provider: z.enum(MODEL_PROVIDERS).optional(),
    agentVersion: z.enum(AGENT_VERSIONS).optional(),
    model: z.enum(PROVIDER_MODELS).optional(),
    thinkingTokenBudget: z.enum(THINKING_LEVELS).optional(),
  })
  .refine(
    (v) =>
      v.provider === undefined ||
      v.model === undefined ||
      (MODELS_BY_PROVIDER_ACCEPTED[v.provider] as readonly ProviderModel[]).includes(v.model),
    { message: "model is not valid for the chosen provider", path: ["model"] },
  );

export type ModelPreferences = z.infer<typeof modelPreferencesSchema>;

/**
 * Effective values when no per-thread override is set: `DEFAULT_ACTOR_MODEL`
 * with any `actor-model-override` rule spread over it. `agentVersion` is
 * fixed at `"v4"`.
 */
export const modelPreferenceDefaultsSchema = z.object({
  provider: z.enum(MODEL_PROVIDERS),
  agentVersion: z.enum(AGENT_VERSIONS),
  model: z.enum(PROVIDER_MODELS),
  thinkingTokenBudget: z.enum(THINKING_LEVELS),
});

export type ModelPreferenceDefaults = z.infer<typeof modelPreferenceDefaultsSchema>;

/**
 * Statsig dynamic config that overrides `DEFAULT_ACTOR_MODEL` for a cohort:
 * an experiment, or a rollback in a pinch without a deploy. It has no
 * dashboard default value, so with no matching rule every thread gets the
 * code default. A rule's value is a partial (`actorModelOverrideSchema`)
 * spread over the default, so a rule that sets only `thinkingTokenBudget`
 * keeps following the default model.
 */
export const ACTOR_MODEL_CONFIG = "actor-model-override";

/**
 * Each field is checked on its own here; whether the provider, model and
 * effort agree is checked after the merge, against `actorModelSchema`.
 */
export const actorModelOverrideSchema = z
  .object({
    provider: z.enum(MODEL_PROVIDERS),
    model: z.enum(PROVIDER_MODELS),
    thinkingTokenBudget: z.enum(THINKING_LEVELS),
    /**
     * `false` keeps the actor on the chosen model alone, with no fallback
     * models behind it. Eval variants set it so a fallback's answer is never
     * published under the variant's label.
     */
    failover: z.boolean(),
  })
  .partial();

/**
 * A fully resolved actor model. The discriminated union over `provider` makes
 * the `model` ↔ `provider` agreement a type-level guarantee — no runtime
 * refine, and downstream code can narrow on `provider` to read `model`.
 */
export const actorModelSchema = z.discriminatedUnion("provider", [
  z.object({
    provider: z.literal("anthropic"),
    model: z.enum(ANTHROPIC_MODELS_ACCEPTED),
    thinkingTokenBudget: z.enum(ANTHROPIC_EFFORT_LEVELS),
  }),
  z.object({
    provider: z.literal("gemini"),
    model: z.enum(GEMINI_MODELS_ACCEPTED),
    thinkingTokenBudget: z.enum(THINKING_TOKEN_BUDGET_LEVELS),
  }),
  z.object({
    provider: z.literal("openai"),
    model: z.enum(OPENAI_MODELS),
    thinkingTokenBudget: z.enum(THINKING_TOKEN_BUDGET_LEVELS),
  }),
  // Present so a per-thread `local` selection round-trips through the same
  // schema as every other provider. Nobody should set this cohort-wide from the
  // Statsig dashboard — the endpoint it needs only exists on a dev machine.
  z.object({
    provider: z.literal("local"),
    model: z.enum(LOCAL_MODELS),
    thinkingTokenBudget: z.enum(THINKING_TOKEN_BUDGET_LEVELS),
  }),
  z.object({
    provider: z.literal("baseten"),
    model: z.enum(BASETEN_MODELS),
    // The ladder, as with the other non-Anthropic providers — but each Baseten
    // model accepts its own subset of reasoning efforts and 400s on the rest,
    // so `basetenService.ts` clamps the resolved level per model rather than
    // passing it through.
    thinkingTokenBudget: z.enum(THINKING_TOKEN_BUDGET_LEVELS),
  }),
]);

export type ActorModel = z.infer<typeof actorModelSchema>;
